import dayjs from 'dayjs';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import type { JobPost } from '~/api/jobPosts/types';
import type { EditorRefHandle } from '~/components';

import { useCreateJobPost } from '~/api/jobPosts/useCreateJobPost';
import { useUpdateJobPost } from '~/api/jobPosts/useUpdateJobPost';
import { Blocker, Container, Header, Separator } from '~/components';
import { Form } from '~/components/Form';
import { env } from '~/constants/env';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import type { JobPostForm } from './types';

import { sectionsStyle } from './CreateEditJobPostForm.css';
import { mapJobPostFormDataToPayload } from './CreateEditJobPostForm.utils';
import { JobPostFormCommandMenuItems } from './JobPostFormCommandMenuItems';
import { JobPostBasicSection } from './sections/JobPostBasicSection';
import { JobPostDetailsSection } from './sections/JobPostDetailsSection';
import { JobPostPlanningSection } from './sections/JobPostPlanningSection';
import { doubleColumnStyle } from './sections/jobPostSections.css';

type Props =
  | {
      jobPost: null;
      mode: 'create';
    }
  | {
      jobPost: JobPost;
      mode: 'edit';
    };

export const CreateEditJobPostForm = ({ jobPost, mode }: Props) => {
  const { t } = useTranslation(['common', 'jobPosts']);
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];

  const createMutation = useCreateJobPost();
  const updateMutation = useUpdateJobPost(jobPost?.id ?? 0);

  const form = useForm<JobPostForm>({
    defaultValues: {
      activityIds: jobPost?.activityIds ?? [],
      startsAt: jobPost?.startsAt ? dayjs(jobPost.startsAt).toDate() : null,
      closesAt: jobPost?.closesAt ? dayjs(jobPost.closesAt).toDate() : null,
      description: jobPost?.description ?? { type: 'doc', content: [] },
      endsAt: jobPost?.endsAt ? dayjs(jobPost.endsAt).toDate() : null,
      experienceLevel: jobPost?.experienceLevel ?? null,
      locationType: jobPost?.locationType ?? 'Onsite',
      price: jobPost?.price ?? null,
      priceUnit: jobPost?.priceType === 'Tbd' ? 'Fixed' : (jobPost?.priceType ?? 'Fixed'),
      region: jobPost?.region ?? null,
      title: jobPost?.title ?? '',
    },
  });
  const {
    formState: { isDirty },
    handleSubmit,
  } = form;

  const editorRef = useRef<EditorRefHandle>(null);

  const onSubmit = handleSubmit((data) => {
    const { mutate } = mode === 'create' ? createMutation : updateMutation;

    mutate(mapJobPostFormDataToPayload(data), {
      onSuccess: ({ id }) => {
        toast.success({ description: t(mode === 'create' ? 'jobPosts:alerts.successCreated' : 'jobPosts:alerts.successUpdated') });
        const redirectPath = mode === 'edit' ? searchParams.get(SearchParamKeys.REDIRECT_PATH) : null;
        navigate(redirectPath ?? r(routes.showJobPost, { jobPostId: id }), { state: bypassBlocker, replace: true });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  });

  return (
    <>
      <Header.Root>
        <Header.Content title={t(`jobPosts:createEdit.title.${mode}`)} />

        <Header.Actions>
          <Header.Action
            icon={mode === 'create' ? 'Send' : 'Save'}
            loading={createMutation.isPending || updateMutation.isPending}
            onClick={onSubmit}
          >
            {mode === 'create' ? t('jobPosts:createEdit.actions.create') : t('common:actions.save')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Blocker isBlocked={isDirty} message={t('jobPosts:createEdit.blocker')} />

      <Container>
        <Form onSubmit={onSubmit}>
          <div className={sectionsStyle}>
            <JobPostBasicSection editorRef={editorRef} form={form} />

            <Separator />

            <div className={doubleColumnStyle}>
              <JobPostDetailsSection form={form} />

              <JobPostPlanningSection form={form} />
            </div>
          </div>
        </Form>
      </Container>

      {(env.IS_LOCAL_DEV_ENV || env.IS_STAGING_ENV) && <JobPostFormCommandMenuItems form={form} />}
    </>
  );
};
